import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Boxes, CheckCircle2, Download, FileText, PackageCheck, Plus, Tag, Trash2, Upload } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { useTenant } from "./AppShell";
import { useLicensed } from "./ModuleGate";
import { Badge, Button, cx, Input } from "./ui";
import { api, ApiError, isDemo, type FbaCarton, type FbaDocumentKind, type FbaOrderInfo, type OrderDetail } from "@/lib/api";
import { fmtNumber, fmtRelative } from "@/lib/format";
import { intlLocale, t, msg } from "@/lib/i18n";

export const DOC_LABEL: Record<FbaDocumentKind, string> = { carton_labels: "Kartonetiketten", pallet_labels: "Palettenetiketten", other: "Sonstiges" };
export const PACKING_LABEL = { seller_cartons: "Kunde gibt Kartons vor", warehouse_packs: "Logiful packt und meldet Kartons" } as const;
const errMsg = (e: unknown) => (e instanceof ApiError ? { description: (e.body as { message?: string })?.message } : undefined);

/** Datei als Base64 (ohne „data:…;base64,“) */
const toBase64 = (f: File) =>
  new Promise<string>((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(String(r.result).split(",")[1] ?? "");
    r.onerror = () => rej(r.error);
    r.readAsDataURL(f);
  });

/** Am Artikel: FNSKU und ob vor dem Versand an Amazon beklebt wird (nur mit Modul Pre-FBA) */
export function ProductFbaSection({ productId }: { productId: string }) {
  const licensed = useLicensed("fba", "prefba");
  const { tenantId } = useTenant();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["product-fba", tenantId, productId], queryFn: () => api.fba.product(tenantId, productId), enabled: licensed });
  const [fnsku, setFnsku] = useState("");
  const [label, setLabel] = useState(false);
  useEffect(() => {
    if (q.data) { setFnsku(q.data.fnsku ?? ""); setLabel(q.data.labelRequired); }
  }, [q.data]);
  const save = useMutation({
    mutationFn: () => api.fba.saveProduct(tenantId, productId, { fnsku: fnsku.trim() || null, labelRequired: label }),
    onSuccess: () => {
      toast.success("Gespeichert", { description: msg("Die FNSKU geht als Artikel-Attribut ans Lager.") });
      void qc.invalidateQueries({ queryKey: ["product-fba", tenantId, productId] });
    },
    onError: (e) => toast.error("Nicht gespeichert", errMsg(e)),
  });
  if (!licensed || !q.data) return null;
  const dirty = (q.data.fnsku ?? "") !== fnsku.trim().toUpperCase() || q.data.labelRequired !== label;
  const valid = !fnsku.trim() || /^[A-Za-z0-9]{10}$/.test(fnsku.trim());
  return (
    <section>
      <div className="flex items-baseline mb-2.5">
        <h3 className="text-[12px] font-semibold text-fg-muted uppercase tracking-wide">{t("Amazon FBA")}</h3>
        {q.data.asin && <span className="ml-auto text-[12px] text-fg-subtle font-mono">{t("ASIN")} {q.data.asin}</span>}
      </div>
      <div className="grid grid-cols-2 gap-3 items-end">
        <label className="block">
          <span className="text-[12px] font-medium text-fg-muted">{t("FNSKU")}</span>
          <Input value={fnsku} onChange={(e) => setFnsku(e.target.value.toUpperCase().slice(0, 10))} placeholder={t("z. B. X001ABCDEF")} className="mt-1 [&_input]:font-mono" invalid={!valid} />
        </label>
        <label className="flex items-center gap-2 text-[13px] pb-2">
          <input type="checkbox" className="accent-ink" checked={label} onChange={(e) => setLabel(e.target.checked)} /> {t("vor dem Versand an Amazon bekleben")}
        </label>
      </div>
      {!valid && <p className="mt-1 text-[11.5px] text-danger">{t("10 Zeichen, Buchstaben und Ziffern")}</p>}
      {label && !fnsku.trim() && <p className="mt-1 text-[11.5px] text-warning">{t("Zum Bekleben wird die FNSKU gebraucht.")}</p>}
      {dirty && (
        <div className="mt-2.5 flex gap-2">
          <Button size="sm" variant="primary" disabled={!valid || (label && !fnsku.trim())} loading={save.isPending} onClick={() => save.mutate()}>{t("FNSKU speichern")}</Button>
          <Button size="sm" variant="ghost" onClick={() => { setFnsku(q.data!.fnsku ?? ""); setLabel(q.data!.labelRequired); }}>{t("Verwerfen")}</Button>
        </div>
      )}
    </section>
  );
}

/** Am Auftrag: Amazon-Sendung, FNSKU-Bekleben, Etiketten-Upload, Kartondaten */
export function OrderFbaSection({ order, fba }: { order: OrderDetail; fba: FbaOrderInfo }) {
  const { tenantId } = useTenant();
  const qc = useQueryClient();
  const docs = useQuery({ queryKey: ["fba-docs", tenantId, order.id], queryFn: () => api.fba.documents(tenantId, order.id) });
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ["fba-docs", tenantId, order.id] });
    void qc.invalidateQueries({ queryKey: ["order", tenantId, order.id] });
    void qc.invalidateQueries({ queryKey: ["fba-shipments", tenantId] });
  };
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploadKind, setUploadKind] = useState<FbaDocumentKind>("carton_labels");
  const upload = useMutation({
    mutationFn: async (f: File) => api.fba.upload(tenantId, order.id, { kind: uploadKind, filename: f.name, data: await toBase64(f) }),
    onSuccess: (r) => {
      toast.success(`${DOC_LABEL[uploadKind]} hochgeladen`, { description: r.missing.length ? `Es fehlen noch: ${r.missing.map((k) => DOC_LABEL[k]).join(", ")}` : "Alle Etiketten da – sie gehen automatisch ans Lager." });
      refresh();
    },
    onError: (e) => toast.error("Upload fehlgeschlagen", errMsg(e)),
  });
  const remove = useMutation({
    mutationFn: (docId: string) => api.fba.deleteDocument(tenantId, order.id, docId),
    onSuccess: () => { toast.success("Entfernt"); refresh(); },
    onError: (e) => toast.error("Nicht möglich", errMsg(e)),
  });
  const [sid, setSid] = useState("");
  const setShipmentId = useMutation({
    mutationFn: () => api.fba.setShipmentId(tenantId, order.id, { shipmentId: sid.trim() }),
    onSuccess: () => { toast.success("Amazon-Sendung gespeichert"); refresh(); },
    onError: (e) => toast.error("Nicht gespeichert", errMsg(e)),
  });
  const pick = (k: FbaDocumentKind) => {
    setUploadKind(k);
    fileRef.current?.click();
  };
  const missing = docs.data?.missing ?? [];
  const closed = ["shipped", "cancelled"].includes(order.status);
  const neededKinds: FbaDocumentKind[] = ["carton_labels", ...(fba.palletized ? (["pallet_labels"] as const) : []), "other"];

  return (
    <section className="rounded-xl border border-border p-4 space-y-4">
      <div className="flex items-start gap-2">
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-[13.5px] flex items-center gap-2"><PackageCheck className="size-4 text-fg-muted" /> {t("Pre-FBA an Amazon")} {fba.destinationCode}</p>
          <p className="text-[12.5px] text-fg-muted mt-0.5">
            {PACKING_LABEL[fba.packing]} · {fba.palletized ? t("auf Paletten") : t("Einzelkartons")}
            {fba.shipmentId ? <> {t("· Sendung")} <span className="font-mono text-fg">{fba.shipmentId}</span></> : ""}
            {fba.referenceId ? <> {t("· Ref")} <span className="font-mono">{fba.referenceId}</span></> : ""}
          </p>
        </div>
        {!closed && (missing.length ? <Badge tone="warning">{t("Etiketten fehlen")}</Badge> : docs.data ? <Badge tone="success" icon={<CheckCircle2 className="size-3" />}>{t("Etiketten vollständig")}</Badge> : null)}
      </div>

      {!fba.shipmentId && !closed && (
        <div className="rounded-lg bg-surface-2/70 p-3">
          <p className="text-[12.5px] text-fg-muted">{t("Amazon-Sendungsnummer nachtragen, sobald die Sendung in Seller Central angelegt ist:")}</p>
          <div className="mt-2 flex gap-2">
            <Input value={sid} onChange={(e) => setSid(e.target.value.toUpperCase())} placeholder={t("FBA15…")} className="max-w-[220px] [&_input]:font-mono" />
            <Button size="sm" disabled={!/^FBA[0-9A-Z]{6,12}$/.test(sid.trim())} loading={setShipmentId.isPending} onClick={() => setShipmentId.mutate()}>{t("Speichern")}</Button>
          </div>
        </div>
      )}

      {fba.fnskuLines.length > 0 && (
        <div>
          <p className="text-[11.5px] font-semibold uppercase tracking-wide text-fg-muted flex items-center gap-1.5"><Tag className="size-3.5" /> {t("FNSKU bekleben (Logiful)")}</p>
          <ul className="mt-1.5 text-[13px] space-y-0.5">
            {fba.fnskuLines.map((l) => (
              <li key={l.sku} className="flex gap-3"><span className="tabular-nums font-semibold w-14 text-right">{fmtNumber(l.quantity)}×</span><span className="font-mono">{l.sku}</span><span className="text-fg-muted">→</span><span className="font-mono">{l.fnsku}</span></li>
            ))}
          </ul>
        </div>
      )}

      {/* Etiketten */}
      <div>
        <p className="text-[11.5px] font-semibold uppercase tracking-wide text-fg-muted flex items-center gap-1.5"><FileText className="size-3.5" /> {t("Amazon-Etiketten")}</p>
        {fba.packing === "warehouse_packs" && !fba.packedCartons && !closed && <p className="mt-1 text-[12.5px] text-fg-muted">{t("Logiful packt zuerst und meldet die Kartons – danach hier die Etiketten aus Seller Central hochladen.")}</p>}
        <ul className="mt-1.5 divide-y divide-border">
          {docs.data?.documents.map((d) => (
            <li key={d.id} className="py-1.5 flex items-center gap-2 text-[13px]">
              <span className="w-32 shrink-0 text-fg-muted">{DOC_LABEL[d.kind]}</span>
              <a href={isDemo ? undefined : api.fba.documentUrl(tenantId, order.id, d.id)} target="_blank" rel="noreferrer" className="min-w-0 flex-1 truncate hover:underline">{d.filename}</a>
              <span className="text-[12px] text-fg-subtle whitespace-nowrap">{d.sentAt ? t("beim Lager") : t("wird übermittelt")} · {fmtRelative(d.createdAt)}</span>
              {!d.sentAt && !closed && <button onClick={() => remove.mutate(d.id)} className="size-7 grid place-items-center rounded-md text-fg-subtle hover:bg-surface-2 hover:text-danger" aria-label={t("{filename} entfernen", { filename: d.filename })}><Trash2 className="size-3.5" /></button>}
            </li>
          ))}
          {docs.data && !docs.data.documents.length && <li className="py-1.5 text-[12.5px] text-fg-subtle">{t("Noch keine Etiketten hochgeladen.")}</li>}
        </ul>
        {!closed && (
          <div className="mt-2 flex flex-wrap gap-2">
            {neededKinds.map((k) => (
              <Button key={k} size="sm" variant={missing.includes(k) ? "primary" : "secondary"} icon={<Upload className="size-3.5" />} loading={upload.isPending && uploadKind === k} onClick={() => pick(k)}>
                {DOC_LABEL[k]} {t("(PDF)")}
              </Button>
            ))}
            <input ref={fileRef} type="file" accept="application/pdf" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) upload.mutate(f); }} />
          </div>
        )}
      </div>

      {/* Kartons */}
      {fba.packing === "warehouse_packs" && <CartonsBlock order={order} fba={fba} onSaved={refresh} />}
    </section>
  );
}

function CartonsBlock({ order, fba, onSaved }: { order: OrderDetail; fba: FbaOrderInfo; onSaved: () => void }) {
  const { tenantId, me } = useTenant();
  const isStaff = me.user.isStaff;
  const [editing, setEditing] = useState(false);
  const cartons = fba.packedCartons ?? [];
  const units = cartons.reduce((s, c) => s + c.lines.reduce((x, l) => x + l.quantity, 0), 0);
  return (
    <div>
      <div className="flex items-center gap-2">
        <p className="text-[11.5px] font-semibold uppercase tracking-wide text-fg-muted flex items-center gap-1.5 flex-1"><Boxes className="size-3.5" /> {t("Kartons")} {cartons.length ? t("· {length} Kartons, {units} Stück", { length: cartons.length, units: fmtNumber(units) }) : ""}</p>
        {cartons.length > 0 && (
          <a href={isDemo ? undefined : api.fba.cartonsCsvUrl(tenantId, order.id)} onClick={isDemo ? () => toast.info("Im Demo-Modus nicht verfügbar") : undefined}>
            <Button size="sm" variant="ghost" icon={<Download className="size-3.5" />}>{t("CSV für Seller Central")}</Button>
          </a>
        )}
        {isStaff && !["shipped", "cancelled"].includes(order.status) && !editing && <Button size="sm" variant="secondary" onClick={() => setEditing(true)}>{cartons.length ? t("Kartons ändern") : t("Kartons erfassen")}</Button>}
      </div>
      {!cartons.length && !editing && <p className="mt-1 text-[12.5px] text-fg-muted">{t("Noch nicht gepackt.")}</p>}
      {cartons.length > 0 && !editing && (
        <table className="mt-1.5 w-full text-[12.5px]">
          <thead className="text-fg-subtle"><tr><th className="text-left font-medium py-1">{t("Karton")}</th><th className="text-left font-medium">{t("Inhalt")}</th><th className="text-right font-medium">{t("Gewicht")}</th><th className="text-right font-medium">{t("Maße (cm)")}</th></tr></thead>
          <tbody>
            {cartons.map((c, i) => (
              <tr key={i} className="border-t border-border align-top">
                <td className="py-1.5 tabular-nums">{i + 1}</td>
                <td className="py-1.5">{c.lines.map((l) => <div key={l.sku}><span className="tabular-nums font-medium">{l.quantity}×</span> <span className="font-mono">{l.sku}</span></div>)}</td>
                <td className="py-1.5 text-right tabular-nums">{c.weightKg.toLocaleString(intlLocale())} {t("kg")}</td>
                <td className="py-1.5 text-right tabular-nums whitespace-nowrap">{c.lengthCm} × {c.widthCm} × {c.heightCm}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {editing && <CartonEditor order={order} initial={cartons} onCancel={() => setEditing(false)} onSaved={() => { setEditing(false); onSaved(); }} />}
    </div>
  );
}

type DraftCarton = { lines: { sku: string; quantity: string }[]; weightKg: string; lengthCm: string; widthCm: string; heightCm: string };
const num = (v: string) => Number(v.replace(",", "."));

/** Logiful: gepackte Kartons erfassen (Inhalt, Gewicht, Maße) – Summe muss der Sendung entsprechen */
function CartonEditor({ order, initial, onCancel, onSaved }: { order: OrderDetail; initial: FbaCarton[]; onCancel: () => void; onSaved: () => void }) {
  const { tenantId } = useTenant();
  const skus = [...new Set(order.lines.filter((l) => !l.addedByRule).map((l) => l.sku))];
  const want = new Map<string, number>();
  for (const l of order.lines.filter((x) => !x.addedByRule)) want.set(l.sku, (want.get(l.sku) ?? 0) + l.quantity);
  const blank = (): DraftCarton => ({ lines: [{ sku: skus[0] ?? "", quantity: "" }], weightKg: "", lengthCm: "", widthCm: "", heightCm: "" });
  const [rows, setRows] = useState<DraftCarton[]>(initial.length ? initial.map((c) => ({ lines: c.lines.map((l) => ({ sku: l.sku, quantity: String(l.quantity) })), weightKg: String(c.weightKg).replace(".", ","), lengthCm: String(c.lengthCm), widthCm: String(c.widthCm), heightCm: String(c.heightCm) })) : [blank()]);
  const packed = new Map<string, number>();
  for (const r of rows) for (const l of r.lines) packed.set(l.sku, (packed.get(l.sku) ?? 0) + (Number(l.quantity) || 0));
  const diff = skus.map((s) => ({ sku: s, want: want.get(s) ?? 0, packed: packed.get(s) ?? 0 })).filter((d) => d.want !== d.packed);
  const complete = rows.every((r) => num(r.weightKg) > 0 && num(r.lengthCm) > 0 && num(r.widthCm) > 0 && num(r.heightCm) > 0 && r.lines.every((l) => l.sku && Number(l.quantity) > 0));
  const save = useMutation({
    mutationFn: () => api.fba.saveCartons(tenantId, order.id, rows.map((r) => ({ lines: r.lines.map((l) => ({ sku: l.sku, quantity: Number(l.quantity) })), weightKg: num(r.weightKg), lengthCm: num(r.lengthCm), widthCm: num(r.widthCm), heightCm: num(r.heightCm) }))),
    onSuccess: () => { toast.success("Kartons gespeichert", { description: msg("Der Kunde sieht sie jetzt und kann die Etiketten erstellen.") }); onSaved(); },
    onError: (e) => toast.error("Nicht gespeichert", errMsg(e)),
  });
  const upd = (i: number, f: (r: DraftCarton) => DraftCarton) => setRows((xs) => xs.map((x, j) => (j === i ? f(x) : x)));
  const small = "h-8 rounded-md border border-border bg-surface px-2 text-[12.5px] tabular-nums outline-none focus:border-border-strong";
  return (
    <div className="mt-2 space-y-2">
      {rows.map((r, i) => (
        <div key={i} className="rounded-lg border border-border p-2.5">
          <div className="flex items-center gap-2 text-[12.5px]">
            <span className="font-semibold w-16">{t("Karton")} {i + 1}</span>
            <input className={cx(small, "w-16 text-right")} value={r.weightKg} onChange={(e) => upd(i, (x) => ({ ...x, weightKg: e.target.value }))} placeholder={t("kg")} aria-label={t("Gewicht kg")} />
            <span className="text-fg-subtle">{t("kg")}</span>
            {(["lengthCm", "widthCm", "heightCm"] as const).map((k, j) => (
              <span key={k} className="inline-flex items-center gap-1">{j > 0 && <span className="text-fg-subtle">×</span>}<input className={cx(small, "w-14 text-right")} value={r[k]} onChange={(e) => upd(i, (x) => ({ ...x, [k]: e.target.value }))} placeholder={["L", "B", "H"][j]} aria-label={["Länge", "Breite", "Höhe"][j]} /></span>
            ))}
            <span className="text-fg-subtle">{t("cm")}</span>
            <button className="ml-auto size-7 grid place-items-center rounded-md text-fg-subtle hover:text-danger" onClick={() => setRows((xs) => xs.filter((_, j) => j !== i))} disabled={rows.length === 1} aria-label={t("Karton entfernen")}><Trash2 className="size-3.5" /></button>
          </div>
          {r.lines.map((l, k) => (
            <div key={k} className="mt-1.5 flex items-center gap-2 text-[12.5px] pl-16">
              <input className={cx(small, "w-16 text-right")} value={l.quantity} onChange={(e) => upd(i, (x) => ({ ...x, lines: x.lines.map((y, m) => (m === k ? { ...y, quantity: e.target.value.replace(/\D/g, "") } : y)) }))} placeholder={t("Stk.")} aria-label={t("Menge")} />
              <span className="text-fg-subtle">×</span>
              <select className={cx(small, "font-mono")} value={l.sku} onChange={(e) => upd(i, (x) => ({ ...x, lines: x.lines.map((y, m) => (m === k ? { ...y, sku: e.target.value } : y)) }))}>
                {skus.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              {r.lines.length > 1 && <button className="size-7 grid place-items-center rounded-md text-fg-subtle hover:text-danger" onClick={() => upd(i, (x) => ({ ...x, lines: x.lines.filter((_, m) => m !== k) }))} aria-label={t("Position entfernen")}><Trash2 className="size-3" /></button>}
            </div>
          ))}
          {skus.length > 1 && <button className="mt-1.5 ml-16 text-[12px] text-fg-muted hover:text-fg inline-flex items-center gap-1" onClick={() => upd(i, (x) => ({ ...x, lines: [...x.lines, { sku: skus.find((s) => !x.lines.some((y) => y.sku === s)) ?? skus[0]!, quantity: "" }] }))}><Plus className="size-3" /> {t("Artikel im Karton")}</button>}
        </div>
      ))}
      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" variant="ghost" icon={<Plus className="size-3.5" />} onClick={() => setRows((xs) => [...xs, { ...xs[xs.length - 1]!, lines: xs[xs.length - 1]!.lines.map((l) => ({ ...l })) }])}>{t("Karton (wie letzter)")}</Button>
        <span className={cx("text-[12px]", diff.length ? "text-warning" : "text-success")}>
          {diff.length ? t("Noch offen: {v}", { v: diff.map((d) => `${d.sku} ${d.packed}/${d.want}`).join(" · ") }) : t("Alle Artikel verpackt")}
        </span>
        <span className="ml-auto flex gap-2">
          <Button size="sm" variant="ghost" onClick={onCancel}>{t("Abbrechen")}</Button>
          <Button size="sm" variant="primary" disabled={!!diff.length || !complete} loading={save.isPending} onClick={() => save.mutate()}>{t("Kartons speichern")}</Button>
        </span>
      </div>
    </div>
  );
}
